import { useEffect, useRef, useState } from "react";
import Image from 'next/image';
import ReactPlayer from "react-player";
import { event } from 'nextjs-google-analytics';

interface VideoPlayerProps {
  videoUrl: string;
  setVideoUrl: (url: string) => void;
  viewport: string | undefined;
  page_title: string;
  page_path: string;
}

const VideoPlayer = (props: VideoPlayerProps) => {
  const { videoUrl, setVideoUrl, viewport } = props;
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(viewport === "desktop" ? { x: window.innerWidth - 200, y: window.innerHeight - 380 } : { x: window.innerWidth - 120, y: (36 + 56 + window.innerWidth) - 180 });
  const videoRef = useRef(null);
  const isDraggingRef = useRef(isDragging);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    const offsetX = e.clientX - position.x;
    const offsetY = e.clientY - position.y;

    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingRef.current) {
        setPosition({
          x: e.clientX - offsetX,
          y: e.clientY - offsetY,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    const touch = e.touches[0];
    const offsetX = touch.clientX - position.x;
    const offsetY = touch.clientY - position.y;

    const handleTouchMove = (e: TouchEvent) => {
      if (isDraggingRef.current) {
        e.preventDefault();
        const touch = e.touches[0];
        setPosition({
          x: touch.clientX - offsetX,
          y: touch.clientY - offsetY,
        });
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };

    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);
  };

  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging])

  const handleControl = async (action: string) => {
    switch (action) {
      case "close":
        event("video_close", {
          event_category: 'Video',
          event_label: videoUrl,
          value: 0,
          page_title: props.page_title,
          page_path: props.page_path
        })
        setVideoUrl("");
        break;

      case "pause":
        event("video_pause", {
          event_category: 'Video',
          event_label: videoUrl,
          value: 0,
          page_title: props.page_title,
          page_path: props.page_path
        })
        break;

      case "play":
        event("video_play", {
          event_category: 'Video',
          event_label: videoUrl,
          value: 1,
          page_title: props.page_title,
          page_path: props.page_path
        })
        break;

      case "fullscreen":
        event("video_fullscreen", {
          event_category: 'Video',
          event_label: videoUrl,
          value: 1,
          page_title: props.page_title,
          page_path: props.page_path
        })
        break;

      case "exitFullscreen":
        event("video_exit_fullscreen", {
          event_category: 'Video',
          event_label: videoUrl,
          value: 0,
          page_title: props.page_title,
          page_path: props.page_path
        })
        break;

      case "ended":
        event("video_ended", {
          event_category: 'Video',
          event_label: videoUrl,
          value: 0,
          page_title: props.page_title,
          page_path: props.page_path
        })
        break;

      case "ready":
        event("video_ready", {
          event_category: 'Video',
          event_label: videoUrl,
          value: 1,
          page_title: props.page_title,
          page_path: props.page_path
        })
        break;

      default:
        break;
    }
  }

  const handleFullscreenChange = () => {
    if (document.fullscreenElement) {
      handleControl("fullscreen");
    } else {
      handleControl("exitFullscreen");
    }
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <div
      className="fixed w-[100px] max-w-[100px] md:w-[150px] md:max-w-[150px] h-fit z-20 cursor-move float-video"
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <Image
        src={process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/close-white.png"}
        alt="close icon"
        width={20}
        height={20}
        quality={100}
        className="cursor-pointer absolute top-2 right-2 z-10 p-1"
        onClick={() => handleControl("close")}
      />
      <ReactPlayer
        ref={videoRef}
        url={videoUrl}
        controls={true}
        playing={true}
        loop={true}
        muted={true}
        playsinline={true}
        width={viewport === "desktop" ? 150 : 100}
        height="auto"
        pip={true}
        onReady={() => handleControl("ready")}
        onEnded={() => handleControl("ended")}
        onPause={() => handleControl("pause")}
        onPlay={() => handleControl("play")}
      />
    </div>
  );
}

export default VideoPlayer
